import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtDate, fmtINR, today } from "@/lib/format";
import { ShieldCheck, Clock, AlertTriangle } from "lucide-react";

const BUCKET_COLORS = {
  "0-45": "text-emerald-600",
  "46-90": "text-warning",
  "91-180": "text-red-500",
  ">180": "text-red-700",
};

export default function MSMEReport() {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [o, s] = await Promise.all([
        api.get(`/reports/msme-outstanding?as_of=${asOf}`),
        api.get("/reports/msme-summary"),
      ]);
      setData(o.data); setSummary(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="msme-report-page">
      <PageHeader eyebrow="Compliance" title="MSME Outstanding Dues (MSMED Act)"
        description="Bills owed to Micro, Small, Medium suppliers. Anything > 45 days is a disclosure item in your financials."
        actions={
          <>
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="rounded-sm h-9 w-40" data-testid="msme-asof" />
            <Button onClick={load} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white">Refresh</Button>
          </>
        } />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(summary).map(([status, n]) => (
            <div key={status} className="card-sharp p-4">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">{status.replace("_", " ")} vendors</div>
              <div className="text-2xl font-heading font-bold mt-1">{n}</div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card-sharp p-5 border-accent border-2">
              <div className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-accent" /> Total outstanding</div>
              <div className="metric-value text-2xl mt-2">{fmtINR(data.totals.total_outstanding)}</div>
              <div className="text-xs text-muted-foreground mt-1">{data.totals.count_total} bills</div>
            </div>
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Beyond 45 days (disclosure required)</div>
              <div className="metric-value text-2xl mt-2 text-red-600">{fmtINR(data.totals.beyond_45_days)}</div>
              <div className="text-xs text-muted-foreground mt-1">{data.totals.count_beyond_45} bills · Interest u/s 16 MSMED Act applies</div>
            </div>
            <div className="card-sharp p-5">
              <div className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Ageing buckets</div>
              <div className="mt-2 space-y-1 text-xs">
                {Object.entries(data.buckets).map(([b, v]) => (
                  <div key={b} className="flex justify-between">
                    <span className={BUCKET_COLORS[b] || ""}>{b} days · {v.count} bills</span>
                    <span className="font-mono tabular-nums">{fmtINR(v.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Bill #</th><th>Date</th><th>Vendor</th><th>MSME</th><th>Udyam #</th><th className="text-right">Total</th><th className="text-right">Outstanding</th><th className="text-right">Age</th><th>Bucket</th></tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-muted-foreground">No outstanding MSME bills — great!</td></tr>}
                {data.rows.map((r) => (
                  <tr key={r.bill_id} className={r.beyond_45_days ? "bg-red-50/30" : ""} data-testid={`msme-row-${r.bill_id}`}>
                    <td className="font-mono text-xs">{r.bill_number}</td>
                    <td className="text-xs">{fmtDate(r.bill_date)}</td>
                    <td className="text-sm font-medium">{r.vendor_name}</td>
                    <td className="text-[10px] font-mono uppercase text-accent">{r.msme_status}</td>
                    <td className="text-xs font-mono">{r.udyam_number || "—"}</td>
                    <td className="text-right tabular-nums">{fmtINR(r.grand_total)}</td>
                    <td className="text-right tabular-nums font-medium">{fmtINR(r.outstanding)}</td>
                    <td className="text-right text-xs">{r.age_days} d</td>
                    <td><span className={`text-[10px] font-mono uppercase ${BUCKET_COLORS[r.bucket] || ""}`}>{r.bucket}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
